import type { Brand, CurrencyFormat, DeepPartial, EasingName, GameCatalog, JurisdictionProfile, LocalizedStrings, MathConfig, ResolvedBrand, ResolvedTheme, SlotOptions, Strings, Theme } from '../types';
import { isColor } from '../util/color';
import { applyBrand, brandCurrency, mergeBrand, validateBrand } from './brand';
import { DEFAULT_CURRENCY, DEFAULT_THEME, deepMerge, mergeTheme } from './defaults';

export interface LoadedConfig {
  /** Theme with the brand applied (what the presenter and HUD use). */
  theme: ResolvedTheme;
  /** Theme as loaded, before branding (kept so brands can be swapped at runtime). */
  baseTheme: ResolvedTheme;
  brand: ResolvedBrand | null;
  math: MathConfig;
  jurisdiction: JurisdictionProfile;
  strings: Strings;
  /** Raw locale strings (before theme/brand overrides), kept for runtime theme/brand swaps. */
  locale: Strings;
  /** Language code the strings were resolved for ('en' when inline strings were given). */
  lang: string;
  currency: CurrencyFormat;
}

const EASINGS: EasingName[] = ['linear', 'quadIn', 'quadOut', 'quadInOut', 'cubicOut', 'backOut', 'elasticOut', 'bounceOut', 'softOut', 'bounceCard'];

/** Default theme id when neither the options nor the brand name one. */
export const DEFAULT_THEME_ID = 'lucky-fruit';

/** Flatten localized strings: base keys plus the `$<lang>` block for the requested language. */
export function pickStrings(localized: LocalizedStrings | undefined, lang: string): Strings {
  const out: Strings = {};
  if (!localized) return out;
  for (const [k, v] of Object.entries(localized)) if (typeof v === 'string') out[k] = v;
  const block = localized[`$${lang}`];
  if (block && typeof block === 'object') Object.assign(out, block);
  return out;
}

/** Base URL (ending with '/') under which themes/, math/, jurisdictions/ and locales/ live. */
export function resolveBasePath(basePath?: string): string {
  const docBase = typeof document !== 'undefined' ? document.baseURI : 'http://localhost/';
  const url = new URL(basePath ?? './', docBase).href;
  return url.endsWith('/') ? url : `${url}/`;
}

async function fetchJson<T>(url: string, notFound: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`${notFound}: ${url} (${res.status})`);
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error(`INVALID_JSON: ${url}`);
  }
}

export async function loadTheme(themeOrName: string | Theme, basePath: string, overrides?: DeepPartial<Theme>): Promise<ResolvedTheme> {
  let json: Theme;
  let baseUrl: string;
  if (typeof themeOrName === 'string') {
    baseUrl = `${basePath}themes/${themeOrName}/`;
    const raw = await fetchJson<Theme>(`${baseUrl}theme.json`, 'THEME_NOT_FOUND');
    json = { ...raw, id: raw.id ?? themeOrName };
  } else {
    json = themeOrName;
    baseUrl = `${basePath}themes/${themeOrName.id}/`;
  }
  if (overrides) json = deepMerge(json, overrides);
  return mergeTheme(DEFAULT_THEME, json, baseUrl);
}

export async function loadBrand(brandOrName: string | Brand | null | undefined, basePath: string, overrides?: DeepPartial<Brand>): Promise<ResolvedBrand | null> {
  if (!brandOrName) return null;
  let json: Brand;
  let baseUrl: string;
  if (typeof brandOrName === 'string') {
    baseUrl = `${basePath}brands/${brandOrName}/`;
    const raw = await fetchJson<Brand>(`${baseUrl}brand.json`, 'BRAND_NOT_FOUND');
    json = { ...raw, id: raw.id ?? brandOrName };
  } else {
    json = brandOrName;
    baseUrl = `${basePath}brands/${brandOrName.id}/`;
  }
  if (overrides) json = deepMerge(json, overrides);
  return mergeBrand(json, baseUrl);
}

/** Game catalog (public/catalog.json): the products a platform can launch. */
export async function loadCatalog(basePath: string): Promise<GameCatalog> {
  const cat = await fetchJson<GameCatalog>(`${basePath}catalog.json`, 'CATALOG_NOT_FOUND');
  if (!Array.isArray(cat.games)) throw new Error('INVALID_CATALOG');
  return cat;
}

async function loadStrings(locale: string | Strings | undefined, basePath: string): Promise<{ strings: Strings; lang: string }> {
  if (locale && typeof locale === 'object') return { strings: { ...locale }, lang: 'en' };
  const lang = locale ?? 'en';
  try {
    return { strings: await fetchJson<Strings>(`${basePath}locales/${lang}.json`, 'LOCALE_NOT_FOUND'), lang };
  } catch (err) {
    if (lang === 'en') throw err;
    console.warn(`[simple-slot] locale "${lang}" not found, falling back to en`);
    return { strings: await fetchJson<Strings>(`${basePath}locales/en.json`, 'LOCALE_NOT_FOUND'), lang: 'en' };
  }
}

export async function loadConfig(o: SlotOptions): Promise<LoadedConfig> {
  const basePath = resolveBasePath(o.basePath);
  const ov = o.overrides ?? {};
  const brand = await loadBrand(o.brand, basePath, ov.brand);
  const [baseTheme, rawMath, rawJurisdiction, loaded] = await Promise.all([
    loadTheme(o.theme ?? brand?.defaultTheme ?? DEFAULT_THEME_ID, basePath, ov.theme),
    typeof o.math === 'object' ? o.math : fetchJson<MathConfig>(`${basePath}math/${o.math ?? 'default'}.json`, 'MATH_NOT_FOUND'),
    typeof o.jurisdiction === 'object'
      ? o.jurisdiction
      : fetchJson<JurisdictionProfile>(`${basePath}jurisdictions/${o.jurisdiction ?? 'default'}.json`, 'JURISDICTION_NOT_FOUND'),
    loadStrings(o.locale, basePath),
  ]);
  const math = ov.math ? deepMerge(rawMath, ov.math) : rawMath;
  const jurisdiction = ov.jurisdiction ? { ...rawJurisdiction, ...ov.jurisdiction } : rawJurisdiction;
  const problems = [...validateMath(math), ...validateTheme(baseTheme, math), ...(brand ? validateBrand(brand) : [])];
  if (problems.length) throw new Error(`INVALID_CONFIG: ${problems.join('; ')}`);
  const theme = applyBrand(baseTheme, brand);
  return {
    theme,
    baseTheme,
    brand,
    math,
    jurisdiction,
    strings: { ...loaded.strings, ...pickStrings(theme.strings, loaded.lang) },
    locale: loaded.strings,
    lang: loaded.lang,
    currency: brandCurrency(DEFAULT_CURRENCY, brand, o.currency),
  };
}

/** Returns a list of problems (empty = valid). */
export function validateTheme(theme: ResolvedTheme, math: MathConfig): string[] {
  const p: string[] = [];
  for (const s of math.symbols) {
    if (!theme.assets.symbols[s.id]?.image) p.push(`theme "${theme.id}": missing assets.symbols.${s.id}.image`);
  }
  const pal = theme.palette as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(pal)) {
    if (k === 'paylines') {
      if (!Array.isArray(v) || v.length === 0) p.push('palette.paylines must be a non-empty array');
      else v.forEach((c, i) => (typeof c === 'string' && isColor(c)) || p.push(`palette.paylines[${i}] is not a colour: ${String(c)}`));
    } else if (typeof v !== 'string' || !isColor(v)) p.push(`palette.${k} is not a colour: ${String(v)}`);
  }
  const t = theme.animations?.timing;
  if (!t?.spin || !EASINGS.includes(t.spin.stopEasing)) p.push(`animations.timing.spin.stopEasing must be one of ${EASINGS.join(', ')}`);
  for (const key of ['portrait', 'landscape'] as const) {
    const l = theme.layout?.[key];
    if (!l) {
      p.push(`layout.${key} missing`);
      continue;
    }
    const nums = { designWidth: l.designWidth, designHeight: l.designHeight, ...l.reels };
    for (const [k, v] of Object.entries(nums)) {
      if (typeof v !== 'number' || !(k === 'x' || k === 'y' || k === 'gapX' || k === 'gapY' || k === 'padding' || k === 'cornerRadius' ? v >= 0 : v > 0)) {
        p.push(`layout.${key}.${k} must be a ${k === 'x' || k === 'y' ? 'non-negative' : 'positive'} number`);
      }
    }
  }
  for (const [ev, def] of Object.entries(theme.sounds ?? {})) {
    if (!def || (!def.src && !def.synth)) p.push(`sounds.${ev} needs src or synth`);
  }
  return p;
}

export function validateMath(math: MathConfig): string[] {
  const p: string[] = [];
  const ids = new Set(math.symbols.map((s) => s.id));
  if (!Array.isArray(math.strips) || math.strips.length !== math.reels) p.push(`strips must have ${math.reels} entries`);
  else {
    math.strips.forEach((strip, i) => {
      if (strip.length < math.rows) p.push(`strips[${i}] shorter than rows`);
      for (const s of strip) if (!ids.has(s)) p.push(`strips[${i}] references unknown symbol ${s}`);
    });
  }
  if (!Array.isArray(math.paylines) || math.paylines.length === 0) p.push('paylines must be non-empty');
  else {
    math.paylines.forEach((line, i) => {
      if (line.length !== math.reels) p.push(`paylines[${i}] must have ${math.reels} entries`);
      for (const row of line) if (!Number.isInteger(row) || row < 0 || row >= math.rows) p.push(`paylines[${i}] row ${row} out of range`);
    });
  }
  if (math.scatter && !ids.has(math.scatter.symbol)) p.push(`scatter.symbol ${math.scatter.symbol} is not a defined symbol`);
  if (math.scatter && math.bonusBuy?.enabled && !(math.scatter.freeSpins[math.bonusBuy.scatterCount] > 0)) p.push('bonusBuy.scatterCount must award free spins');
  for (const s of math.symbols) {
    for (const k of Object.keys(s.pays ?? {})) {
      const n = Number(k);
      if (!Number.isInteger(n) || n < 2 || n > math.reels) p.push(`symbol ${s.id} pays key ${k} out of range`);
    }
  }
  if (!Array.isArray(math.bets) || math.bets.length === 0) p.push('bets must be non-empty');
  else {
    for (let i = 1; i < math.bets.length; i++) if (math.bets[i] <= math.bets[i - 1]) p.push('bets must be ascending');
    if (!math.bets.includes(math.defaultBet)) p.push('defaultBet must be one of bets');
  }
  const w = math.winTiers;
  if (!w || !(w.big < w.mega && w.mega < w.epic)) p.push('winTiers must be ascending big < mega < epic');
  if (!(math.maxWinMultiplier > 0)) p.push('maxWinMultiplier must be positive');
  if (!(math.freeSpins?.multiplier > 0)) p.push('freeSpins.multiplier must be positive');
  if (math.evaluator !== 'lines') p.push(`evaluator "${math.evaluator}" not supported (only "lines")`);
  if (math.payBothWays) console.warn('[simple-slot] payBothWays is not implemented yet; ignored');
  return p;
}
